<template>
  <div
    role="status"
    :aria-label="label || $t('general_loading')"
    class="animate-pulse"
    :class="[containerClass]"
  >
    <!-- Block-level skeleton (default) -->
    <template v-if="variant === 'block'">
      <div
        class="rounded-theme-md bg-theme-surface-raised dark:bg-theme-surface-raised"
        :style="{ height: height || '100%', width: width || '100%' }"
      />
    </template>

    <!-- Text lines skeleton -->
    <template v-else-if="variant === 'text'">
      <div class="space-y-2">
        <div
          v-for="i in lines"
          :key="i"
          class="h-3 rounded bg-theme-surface-raised dark:bg-theme-surface-raised"
          :style="{ width: i === lines ? '60%' : '100%' }"
        />
      </div>
    </template>

    <!-- Card skeleton: header + body -->
    <template v-else-if="variant === 'card'">
      <div class="rounded-theme-md border border-theme-border bg-theme-surface p-4 dark:border-theme-border dark:bg-theme-surface">
        <div class="mb-4 flex items-center gap-3">
          <div class="h-9 w-9 flex-shrink-0 rounded-full bg-theme-surface-raised dark:bg-theme-surface-raised" />
          <div class="flex-1 space-y-1.5">
            <div class="h-3 w-2/3 rounded bg-theme-surface-raised dark:bg-theme-surface-raised" />
            <div class="h-2.5 w-1/3 rounded bg-theme-bg-subtle dark:bg-theme-surface-raised" />
          </div>
        </div>
        <div class="space-y-2">
          <div class="h-3 rounded bg-theme-bg-subtle dark:bg-theme-surface-raised" />
          <div class="h-3 rounded bg-theme-bg-subtle dark:bg-theme-surface-raised" />
          <div class="h-3 w-3/4 rounded bg-theme-bg-subtle dark:bg-theme-surface-raised" />
        </div>
      </div>
    </template>

    <!-- Spinner / inline loading indicator -->
    <template v-else>
      <div class="flex items-center justify-center gap-2" :class="[sizeClass]">
        <svg
          class="animate-spin text-theme-palette-emerald"
          :class="iconSizeClass"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span v-if="label" class="text-sm font-medium text-theme-text-secondary">{{ label }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue';

/**
 * AppLoadingSkeleton — generic loading state component.
 *
 * Variants:
 *  - `spinner` (default) — animated spinner with optional label
 *  - `block`             — pulsing rectangle of given width/height
 *  - `text`              — pulsing text lines
 *  - `card`              — pulsing card with avatar + lines
 */
export default {
  name: 'AppLoadingSkeleton',

  props: {
    /** Visual variant of the skeleton */
    variant: { type: String, default: 'spinner' },
    /** Screen-reader label / visible label (spinner) */
    label: { type: String, default: '' },
    /** For block/rectangle variant */
    width: { type: String, default: '' },
    height: { type: String, default: '' },
    /** Number of text lines (text variant) */
    lines: { type: Number, default: 3 },
    /** Size class for the spinner */
    size: { type: String, default: 'md' },
    /** Extra classes on the outer container */
    containerClass: { type: [String, Array, Object], default: '' },
  },

  setup(props) {
    const sizeClass = computed(() => {
      const sizes = { sm: 'py-4', md: 'py-10', lg: 'py-20' };
      return sizes[props.size] || sizes.md;
    });

    const iconSizeClass = computed(() => {
      const sizes = { sm: 'h-4 w-4', md: 'h-7 w-7', lg: 'h-10 w-10' };
      return sizes[props.size] || sizes.md;
    });

    return { sizeClass, iconSizeClass };
  },
};
</script>
